/**
 * The workout's own menu — every act on a day that the 07.09 programator mock draws no control for.
 *
 * It was a ⋯ button in the card header until 8 September 2026. The mock's header carries a grip, a
 * title, a duplicate and an ×, and nothing outside the mock may be visible, so the trigger is gone
 * and the menu is a CONTEXT menu on the card itself: right-click it, or press the keyboard menu key
 * while any control inside it has focus. `contextmenu` is dispatched at the focused element and
 * bubbles, so the two header icons and the rename field all reach this.
 *
 * **It is a full equivalent of the drag, not a fallback** — view ADR §12 requires that, and
 * `e2e/keyboard-move.spec.ts` pins all seven weekdays with the current one refused-but-focusable.
 * It also carries the two acts that now have no visible affordance at all: "Programuj dzień", which
 * the mock reaches by pressing the card body, and the rename, which the mock does not draw.
 *
 * **The cost is stated rather than hidden:** a context menu has no visible mark, so a trainer who
 * does not try right-clicking will not discover the rename. The mock draws no affordance for it
 * either, which is why this is the shape it takes; if the owner wants it findable, the answer is a
 * mark in the mock, not a control invented here.
 *
 * **Discoverability is one problem; REACHABILITY was another, and it is fixed.** The paragraph above
 * accounted for the first and quietly assumed the second. It does not hold on a Mac: a MacBook
 * keyboard has no ContextMenu key, and the browser maps nothing else to `contextmenu`, so a
 * keyboard-only trainer on this hardware had no route at all to "Programuj dzień", the rename, the
 * duplicate or the delete — the move survived through the grip (since removed), the other four did not.
 * `SessionCard` therefore binds **Shift+F10** on the card, which is the platform-independent name for
 * this gesture, and re-dispatches it as a real `contextmenu` so both paths run the same code. No
 * visible affordance is added, so the mock is untouched.
 */

import { ContextMenu } from '@base-ui/react/context-menu';
import type { ReactElement } from 'react';

import { useMenuLift, type MenuLift } from '~/shared/ui/dnd/useMenuLift';

import type { DayActionsView } from '../model/builderChrome';
import type { MoveTargetView } from '../model/dayCardView';

import menuStyles from '~/shared/ui/MenuSurface.module.css';

export interface SessionActionsProps {
  /** The words and the refusals, one answer for the whole canvas. */
  readonly view: DayActionsView;
  readonly onDuplicate: () => void;
  /** Raises the confirm. Nothing is written until it is answered. */
  readonly onRequestDelete: () => void;
}

interface SessionMenuProps {
  /** The element the menu opens on — the card. Rendered as the context-menu trigger. */
  readonly trigger: ReactElement;
  /**
   * "Programuj dzień", and the dispatch behind it. `null` when the plan cannot be edited.
   */
  readonly program: {
    readonly label: string;
    readonly onSelect: () => void;
  } | null;
  /** "Zmień nazwę treningu", and the dispatch behind it. `null` when the plan cannot be edited. */
  readonly rename: {
    readonly label: string;
    readonly onSelect: () => void;
  } | null;
  /**
   * "Przestaw" — the keyboard lift, which started from the header grip until the owner removed it
   * on 15 September 2026. `null` when the plan cannot be edited.
   */
  readonly lift: MenuLift | null;
  /** "Przenieś na dzień…" / "Przenieś w cyklu…" — the group's own heading inside the popup. */
  readonly moveLabel: string;
  readonly targets: readonly MoveTargetView[];
  /** Why nothing in the MOVE group may act — a consumed phase, or a phase with one training. */
  readonly disabledReason: string | null;
  /** Dispatches the stop's own coordinate — a weekday on the grid, a position on the tape. */
  readonly onMove: (coordinate: number) => void;
  /** The structural acts, or `null` when the document is not editable. */
  readonly actions: SessionActionsProps | null;
}

function MenuAction({
  label,
  reason,
  onSelect,
}: {
  readonly label: string;
  readonly reason: string | null;
  readonly onSelect: () => void;
}) {
  return (
    <ContextMenu.Item
      className={menuStyles.item}
      disabled={reason !== null}
      onClick={() => {
        if (reason === null) onSelect();
      }}
    >
      <span className={menuStyles.itemLabel}>
        {label}
        {reason !== null && <span className={menuStyles.reason}>{reason}</span>}
      </span>
    </ContextMenu.Item>
  );
}

export function SessionMenu({
  trigger,
  program,
  rename,
  lift,
  moveLabel,
  targets,
  disabledReason,
  onMove,
  actions,
}: SessionMenuProps) {
  const menuLift = useMenuLift(lift);
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger render={trigger} />
      <ContextMenu.Portal>
        <ContextMenu.Positioner className={menuStyles.positioner}>
          <ContextMenu.Popup className={menuStyles.popup} finalFocus={menuLift.finalFocus}>
            {program !== null && (
              <MenuAction label={program.label} reason={null} onSelect={program.onSelect} />
            )}
            {rename !== null && (
              <MenuAction label={rename.label} reason={null} onSelect={rename.onSelect} />
            )}
            {(program !== null || rename !== null) && (
              <ContextMenu.Separator className={menuStyles.separator} />
            )}

            {/* Outside the move group: it is the way into the arrow keys, not one of the stops. */}
            {lift !== null && (
              <MenuAction label={lift.label} reason={lift.disabledReason} onSelect={menuLift.select} />
            )}
            <ContextMenu.Group>
              <ContextMenu.GroupLabel className={menuStyles.groupLabel}>
                {moveLabel}
              </ContextMenu.GroupLabel>
              {targets.map((target) => {
                const reason = disabledReason ?? target.disabledReason;
                return (
                  <ContextMenu.Item
                    key={target.coordinate}
                    className={menuStyles.item}
                    disabled={reason !== null}
                    onClick={() => {
                      if (reason === null) onMove(target.coordinate);
                    }}
                  >
                    <span className={menuStyles.itemLabel}>
                      {target.label}
                      {reason !== null && <span className={menuStyles.reason}>{reason}</span>}
                    </span>
                  </ContextMenu.Item>
                );
              })}
            </ContextMenu.Group>

            {actions !== null && (
              <>
                <ContextMenu.Separator className={menuStyles.separator} />
                <ContextMenu.Group>
                  <ContextMenu.GroupLabel className={menuStyles.groupLabel}>
                    {actions.view.group}
                  </ContextMenu.GroupLabel>
                  <MenuAction
                    label={actions.view.duplicateLabel}
                    reason={actions.view.duplicateRefusal}
                    onSelect={actions.onDuplicate}
                  />
                  {/* No danger ink on the entry itself — the precedent is the day dock's own
                   * "Usuń ćwiczenie", and the loud treatment belongs on the confirm that follows. */}
                  <MenuAction
                    label={actions.view.deleteLabel}
                    reason={null}
                    onSelect={actions.onRequestDelete}
                  />
                </ContextMenu.Group>
              </>
            )}
          </ContextMenu.Popup>
        </ContextMenu.Positioner>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}
